// Resize/compress images in the browser before uploading to S3.
// A 4000px phone photo (~8MB) typically becomes ~300–600KB → much faster uploads.
const MAX_DIMENSION = 1600;
const QUALITY = 0.85;
const SKIP_BELOW_BYTES = 300 * 1024; // files under 300KB are already fine

export async function compressImage(file) {
  // Only compress still images — keep GIF animation and videos untouched
  if (!file.type.startsWith("image/")) return file;
  if (file.type === "image/gif") return file;
  if (file.size <= SKIP_BELOW_BYTES) return file;

  let img;
  try {
    img = await createImageBitmap(file);
  } catch {
    return file; // browser can't decode — upload original
  }

  const { width, height } = img;
  const scale = Math.min(1, MAX_DIMENSION / Math.max(width, height));
  if (scale === 1 && file.type !== "image/png") return file; // nothing to gain

  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  // Keep PNG as PNG (preserves transparency); JPEG for everything else
  const type = file.type === "image/png" ? "image/png" : "image/jpeg";
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, type, QUALITY));

  // If compression didn't help, keep the original
  if (!blob || blob.size >= file.size) return file;

  const ext = type === "image/png" ? ".png" : ".jpg";
  const base = file.name.replace(/\.[^.]+$/, "") || "image";
  return new File([blob], `${base}${ext}`, { type });
}
